import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  updateProfile,
  uploadProfileImage,
  removeProfileImage,
  confirmCurrentPassword,
  changePassword,
  resetSettingsPasswordFlow,
} from "../../redux/action/settingAction/SettingAction";
import { checkSession, logoutUser } from "../../redux/action/authAction/AuthAction";
import Button from "../../common/Buttons";
import Avatar from "../../common/Avatar";
import Modal from "../../common/Modal";
import FormField from "../../common/FormField";
import Toaster, { notify } from "../../common/Toast";
import {
  LuCheck,
  LuEye,
  LuEyeOff,
  LuKeyRound,
  LuPencilLine,
  LuTrash2,
  LuX,
} from "react-icons/lu";

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;


function PasswordInputField({
  label,
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  autoFocus,
  autoComplete,
}) {
  const [show, setShow] = useState(false);

  return (
    <div>
      <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
        {label}
      </label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          onChange={onChange}
          onBlur={onBlur}
          className={`h-9 w-full rounded-md border px-3 pr-9 text-[13px] outline-none focus:ring-2 ${
            error
              ? "border-red-300 focus:border-red-400 focus:ring-red-100"
              : "border-slate-300 focus:border-[#5B7CFF] focus:ring-[#5B7CFF]/20"
          }`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        >
          {show ? <LuEyeOff size={16} /> : <LuEye size={16} />}
        </button>
      </div>
      {error && <p className="mt-1.5 text-[12px] text-red-500">{error}</p>}
    </div>
  );
}

export default function Profile() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const {
    updateProfileLoading,
    uploadImageLoading,
    removeImageLoading,
    confirmPasswordLoading,
    confirmPasswordError,
    changePasswordLoading,
  } = useSelector((state) => state.settings);

  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [touched, setTouched] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const markTouched = (field) => () => setTouched((t) => ({ ...t, [field]: true }));

  const { currentPassword, newPassword, confirmNewPassword } = changePasswordForm;

  // .................... Password validation (live) ...........................

  const invalidNewPassword =
    newPassword.length > 0 &&
    (newPassword.length < 8 ||
      newPassword.length > 32 ||
      !PASSWORD_REGEX.test(newPassword));

  const samePasswordAsCurrent =
    !!newPassword && !!currentPassword && newPassword === currentPassword;

  const passwordsDoNotMatch =
    !!newPassword && !!confirmNewPassword && newPassword !== confirmNewPassword;

  const currentPasswordError =
    confirmPasswordError?.message ||
    (touched.currentPassword && !currentPassword.trim() ? "Current password is required." : "");

  const newPasswordError =
    touched.newPassword && !newPassword
      ? "New password is required."
      : invalidNewPassword
      ? "Password must be 8-32 characters and include upper & lower case letters, a number, and a special character."
      : samePasswordAsCurrent
      ? "New password must be different from your current password."
      : "";

  const confirmNewPasswordError =
    touched.confirmNewPassword && !confirmNewPassword
      ? "Please confirm your new password."
      : passwordsDoNotMatch
      ? "Passwords do not match."
      : "";

  const isPasswordFormValid =
    currentPassword.trim() !== "" &&
    newPassword !== "" &&
    confirmNewPassword !== "" &&
    !invalidNewPassword &&
    !samePasswordAsCurrent &&
    !passwordsDoNotMatch;

  // .................... Profile field commits ...........................

  const commitField = (field) => (newValue) => {
    dispatch(updateProfile({ ...user, [field]: newValue })).then((res) => {
      if (res.success) {
        notify.success("Profile updated", `Your ${field} has been saved.`);
      } else {
        notify.error("Update failed", res.error);
      }
    });
  };

  // .................... Image upload ...........................

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
  };

  const confirmUpload = () => {
    dispatch(uploadProfileImage(pendingFile, user.email)).then((res) => {
      if (res.success) {
        notify.success("Image uploaded", "Your profile picture has been updated.");
        setTimeout(() => window.location.reload(), 1200);
      } else {
        notify.error("Upload failed", res.error);
      }
      setPendingFile(null);
      setPendingPreview(null);
    });
  };

  const cancelUpload = () => {
    setPendingFile(null);
    setPendingPreview(null);
  };

  const handleImageRemove = () => {
    dispatch(removeProfileImage(user.email)).then((res) => {
      if (res.success) {
        notify.success("Image removed", "Your profile picture has been removed.");
        setTimeout(() => window.location.reload(), 1200);
      } else {
        notify.error("Remove failed", res.error);
      }
    });
  };

    const [checkingSession, setCheckingSession] = useState(true);
  
    useEffect(() => {
      dispatch(checkSession()).finally(() => setCheckingSession(false));
    }, [dispatch]);

  // .................... Password flow ...........................

  const openPasswordModal = () => setPasswordModalOpen(true);

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    setTouched({ currentPassword: false, newPassword: false, confirmNewPassword: false });
    setChangePasswordForm({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
    dispatch(resetSettingsPasswordFlow());
  };

  const handleSubmitPasswordChange = async () => {
    if (!isPasswordFormValid) return;
    const confirmRes = await dispatch(
      confirmCurrentPassword(user.email, changePasswordForm.currentPassword),
    );
    if (!confirmRes.success) {
      setChangePasswordForm((f) => ({ ...f, currentPassword: "" }));
      return;
    }

    const changeRes = await dispatch(
      changePassword(user.email, changePasswordForm.newPassword),
    );
    if (changeRes.success) {
      notify.success("Password changed", "Please log in again with your new password.");
      closePasswordModal();
      setTimeout(() => {
        dispatch(logoutUser());
      }, 1200);
    } else {
      notify.error("Change failed", changeRes.error);
    }
  };

  if (!user) {
    return <p className="p-6 text-sm text-slate-400">Loading profile…</p>;
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 pb-10">
      <div className="grid gap-5 lg:grid-cols-3">
        {/* ---------------- Left: Avatar card ---------------- */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center rounded-xl border border-slate-200 bg-white p-7 text-center shadow-sm lg:col-span-1"
        >
          <Avatar name={user.name} src={user.profileImageUrl} size={96} />

          <h3 className="mt-4 text-lg font-bold text-slate-900">{user.name}</h3>
          <p className="mt-1 text-[13px] text-slate-500">{user.email}</p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            variant="solid"
            size="sm"
            icon={<LuPencilLine size={14} />}
            loading={uploadImageLoading}
            onClick={() => fileInputRef.current?.click()}
            className="mt-5"
          >
            Edit Profile
          </Button>

          {user.profileImageUrl && (
            <button
              onClick={handleImageRemove}
              disabled={removeImageLoading}
              className="mt-2 flex items-center gap-1 text-[12px] text-red-500 hover:underline disabled:opacity-50"
            >
              <LuTrash2 size={12} />
              {removeImageLoading ? "Removing…" : "Remove photo"}
            </button>
          )}

          {pendingPreview && (
            <div className="mt-4 flex w-full items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-2 text-left">
              <img src={pendingPreview} alt="Preview" className="h-12 w-12 rounded-md object-cover" />
              <span className="flex-1 truncate text-[12px] text-slate-500">{pendingFile?.name}</span>
              <button
                onClick={confirmUpload}
                className="rounded-md border border-emerald-300 p-1.5 text-emerald-600 hover:bg-emerald-50"
              >
                <LuCheck size={15} />
              </button>
              <button
                onClick={cancelUpload}
                className="rounded-md border border-red-300 p-1.5 text-red-500 hover:bg-red-50"
              >
                <LuX size={15} />
              </button>
            </div>
          )}
        </motion.section>

        {/* ---------------- Right: Profile details card ---------------- */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7 lg:col-span-2"
        >
          <h2 className="mb-5 text-lg font-semibold text-slate-900">Profile Details</h2>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <FormField label="Name" value={user.name} required onCommit={commitField("name")} />
            </div>
            <FormField label="Contact Number" phone value={user.contactNumber} onCommit={commitField("contactNumber")} />
            <FormField label="Designation" value={user.designation} onCommit={commitField("designation")} />
            <div className="sm:col-span-2">
              <FormField label="Address" value={user.address} onCommit={commitField("address")} />
            </div>
          </div>

          {updateProfileLoading && (
            <p className="mt-3 text-[12px] text-slate-400">Saving…</p>
          )}
        </motion.section>
      </div>

      {/* ---------------- Security card ---------------- */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7"
      >
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Security</h2>
        <Button variant="solid" icon={<LuKeyRound size={15} />} onClick={openPasswordModal}>
          Change Password
        </Button>
      </motion.section>

      {/* ---------------- Password modal ---------------- */}
      <Modal
        open={passwordModalOpen}
        onClose={closePasswordModal}
        title="Change Account Password"
        footer={
          <Button
            variant="solid"
            tone="success"
            icon={<LuCheck size={15} />}
            loading={confirmPasswordLoading || changePasswordLoading}
            disabled={!isPasswordFormValid}
            onClick={handleSubmitPasswordChange}
          >
            Save
          </Button>
        }
      >
        <PasswordInputField
          label="Current Password"
          placeholder="Current Password"
          autoComplete="current-password"
          autoFocus
          value={currentPassword}
          onChange={(e) => {
            setChangePasswordForm((f) => ({ ...f, currentPassword: e.target.value }));
            if (confirmPasswordError) dispatch(resetSettingsPasswordFlow());
          }}
          onBlur={markTouched("currentPassword")}
          error={currentPasswordError}
        />

        <PasswordInputField
          label="New Password"
          placeholder="New Password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) =>
            setChangePasswordForm((f) => ({ ...f, newPassword: e.target.value }))
          }
          onBlur={markTouched("newPassword")}
          error={newPasswordError}
        />

        <PasswordInputField
          label="Confirm New Password"
          placeholder="Confirm New Password"
          autoComplete="new-password"
          value={confirmNewPassword}
          onChange={(e) =>
            setChangePasswordForm((f) => ({ ...f, confirmNewPassword: e.target.value }))
          }
          onBlur={markTouched("confirmNewPassword")}
          error={confirmNewPasswordError}
        />
      </Modal>

      <Toaster />
    </div>
  );
}